import { useEffect, useState } from "react";
import api, { money, apiErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Car, Plus, Wrench, Wallet, Receipt, TrendingUp, TrendingDown, Trash2, Pencil, Route } from "lucide-react";
import { toast } from "sonner";

const EXP_CATEGORIES = ["Diesel", "Toll Tax", "Repairing", "Loading/Unloading", "RTO Challan", "Driver Salary", "Other"];
const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "2-digit" }) : "—");

const Stat = ({ label, value, accent, icon: Icon }) => (
  <div className="border border-border p-3 bg-background">
    <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-muted-foreground">{Icon && <Icon className={`h-3 w-3 ${accent || ""}`} />}{label}</div>
    <div className={`font-mono-num text-lg font-bold mt-1 ${accent || ""}`}>{typeof value === "number" ? money(value) : value}</div>
  </div>
);

export default function VehiclesPage() {
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // vehicle obj or {} for new
  const [manageId, setManageId] = useState(null);

  const load = () => api.get("/vehicles").then((r) => setVehicles(r.data)).catch((e) => toast.error(apiErr(e))).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const totals = vehicles.reduce((a, v) => ({
    earnings: a.earnings + (v.summary?.earnings || 0),
    expenses: a.expenses + (v.summary?.expenses || 0),
    profit: a.profit + (v.summary?.profit || 0),
  }), { earnings: 0, expenses: 0, profit: 0 });

  const del = async (v) => {
    if (!window.confirm(`Delete vehicle ${v.plate_number}? This removes its expense ledger.`)) return;
    try { await api.delete(`/vehicles/${v.id}`); toast.success("Vehicle deleted"); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };

  return (
    <div className="p-6 space-y-6" data-testid="vehicles-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-heading text-3xl flex items-center gap-2"><Car className="h-7 w-7 text-primary" /> Vehicle Management</h1>
          <p className="text-muted-foreground text-sm">Manage your own fleet, track running expenses &amp; see profit/loss per vehicle across self bookings.</p>
        </div>
        <Button className="rounded-none gap-2 font-bold uppercase tracking-wide" onClick={() => setEditing({})} data-testid="add-vehicle-btn"><Plus className="h-4 w-4" /> Add Vehicle</Button>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Stat label="Fleet Size" value={String(vehicles.length)} icon={Car} />
        <Stat label="Total Earnings" value={totals.earnings} accent="text-emerald-500" icon={TrendingUp} />
        <Stat label="Total Expenses" value={totals.expenses} accent="text-primary" icon={Receipt} />
        <Stat label="Net Profit / Loss" value={totals.profit} accent={totals.profit >= 0 ? "text-emerald-500" : "text-red-500"} icon={Wallet} />
      </div>

      {loading ? (
        <div className="p-8 text-center text-muted-foreground">Loading fleet…</div>
      ) : vehicles.length === 0 ? (
        <div className="border border-border p-10 text-center text-muted-foreground" data-testid="vehicles-empty">No vehicles yet. Click <b>Add Vehicle</b> to register your fleet.</div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {vehicles.map((v) => {
            const s = v.summary || { trips: 0, earnings: 0, expenses: 0, profit: 0 };
            return (
              <div key={v.id} className="border border-border bg-card flex flex-col" data-testid={`vehicle-card-${v.id}`}>
                <div className="p-4 border-b border-border">
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <div className="font-mono-num font-black text-lg tracking-wide">{v.plate_number}</div>
                      <div className="text-sm text-muted-foreground">{v.model || "—"}</div>
                    </div>
                    {v.type && <span className="text-[10px] font-bold uppercase px-2 py-0.5 bg-muted border border-border">{v.type}</span>}
                  </div>
                  {v.maintenance_alert && (
                    <div className="mt-3 flex items-start gap-2 text-xs bg-amber-500/10 border border-amber-500/40 text-amber-600 dark:text-amber-400 px-2 py-1.5" data-testid={`vehicle-alert-${v.id}`}>
                      <Wrench className="h-3.5 w-3.5 mt-0.5 shrink-0" /> <span>{v.maintenance_alert}</span>
                    </div>
                  )}
                </div>
                <div className="p-4 grid grid-cols-2 gap-2 flex-1">
                  <Stat label="Trips" value={String(s.trips)} icon={Route} />
                  <Stat label="Earnings" value={s.earnings} accent="text-emerald-500" />
                  <Stat label="Expenses" value={s.expenses} accent="text-muted-foreground" />
                  <Stat label="P / L" value={s.profit} accent={s.profit >= 0 ? "text-emerald-500" : "text-red-500"} icon={s.profit >= 0 ? TrendingUp : TrendingDown} />
                </div>
                <div className="p-3 border-t border-border flex gap-2">
                  <Button size="sm" className="rounded-none flex-1 text-xs font-bold uppercase" onClick={() => setManageId(v.id)} data-testid={`vehicle-manage-${v.id}`}>Manage &amp; Expenses</Button>
                  <Button size="icon" variant="outline" className="rounded-none h-9 w-9" onClick={() => setEditing(v)} data-testid={`vehicle-edit-${v.id}`}><Pencil className="h-4 w-4" /></Button>
                  <Button size="icon" variant="ghost" className="rounded-none h-9 w-9 text-destructive" onClick={() => del(v)} data-testid={`vehicle-del-${v.id}`}><Trash2 className="h-4 w-4" /></Button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {editing && <VehicleDialog vehicle={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
      {manageId && <ManageDialog vehicleId={manageId} onClose={() => setManageId(null)} onChanged={load} />}
    </div>
  );
}

function VehicleDialog({ vehicle, onClose, onSaved }) {
  const isNew = !vehicle.id;
  const [f, setF] = useState({
    plate_number: vehicle.plate_number || "", model: vehicle.model || "",
    type: vehicle.type || "", maintenance_alert: vehicle.maintenance_alert || "",
  });
  const [busy, setBusy] = useState(false);

  const save = async () => {
    if (!f.plate_number.trim()) return toast.error("Plate number is required");
    setBusy(true);
    try {
      if (isNew) await api.post("/vehicles", f);
      else await api.put(`/vehicles/${vehicle.id}`, f);
      toast.success(isNew ? "Vehicle added" : "Vehicle updated"); onSaved();
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md rounded-none" data-testid="vehicle-dialog">
        <DialogHeader><DialogTitle className="font-heading text-2xl">{isNew ? "Add Vehicle" : "Edit Vehicle"}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1"><Label className="text-xs">Plate Number *</Label><Input className="rounded-none bg-card h-10 font-mono-num uppercase" value={f.plate_number} onChange={(e) => setF({ ...f, plate_number: e.target.value })} placeholder="e.g. MH01AB1234" data-testid="vehicle-plate" /></div>
          <div className="space-y-1"><Label className="text-xs">Model</Label><Input className="rounded-none bg-card h-10" value={f.model} onChange={(e) => setF({ ...f, model: e.target.value })} placeholder="e.g. Tata 407" data-testid="vehicle-model" /></div>
          <div className="space-y-1"><Label className="text-xs">Type</Label><Input className="rounded-none bg-card h-10" value={f.type} onChange={(e) => setF({ ...f, type: e.target.value })} placeholder="e.g. LCV / Container / Open Truck" data-testid="vehicle-type" /></div>
          <div className="space-y-1"><Label className="text-xs">Maintenance Alert</Label><Input className="rounded-none bg-card h-10" value={f.maintenance_alert} onChange={(e) => setF({ ...f, maintenance_alert: e.target.value })} placeholder="e.g. Service due 15 Jul / Insurance expiring" data-testid="vehicle-alert" /></div>
        </div>
        <div className="flex justify-end gap-2 mt-2">
          <Button variant="outline" className="rounded-none" onClick={onClose}>Cancel</Button>
          <Button className="rounded-none font-bold uppercase" onClick={save} disabled={busy} data-testid="vehicle-save">{busy ? "Saving…" : "Save"}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function ManageDialog({ vehicleId, onClose, onChanged }) {
  const [d, setD] = useState(null);
  const [exp, setExp] = useState({ category: "Diesel", amount: "", remarks: "", date: "" });
  const [busy, setBusy] = useState(false);

  const load = () => api.get(`/vehicles/${vehicleId}`).then((r) => setD(r.data)).catch((e) => toast.error(apiErr(e)));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [vehicleId]);

  const addExpense = async () => {
    const amt = parseFloat(exp.amount);
    if (isNaN(amt) || amt <= 0) return toast.error("Enter a valid amount");
    setBusy(true);
    try {
      await api.post(`/vehicles/${vehicleId}/expenses`, { category: exp.category, amount: amt, remarks: exp.remarks.trim(), date: exp.date });
      setExp({ category: "Diesel", amount: "", remarks: "", date: "" });
      toast.success("Expense recorded"); await load(); onChanged?.();
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  const delExpense = async (e) => {
    if (e.source !== "vehicle") return;
    try { await api.delete(`/vehicles/${vehicleId}/expenses/${e.id}`); await load(); onChanged?.(); }
    catch (err) { toast.error(apiErr(err)); }
  };

  const s = d?.summary || { trips: 0, earnings: 0, expenses: 0, profit: 0 };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl rounded-none max-h-[92vh] overflow-hidden flex flex-col" data-testid="vehicle-manage-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl">
            {d ? <>{d.vehicle.plate_number} <span className="text-muted-foreground text-lg">· {d.vehicle.model || "—"}</span></> : "Loading…"}
          </DialogTitle>
        </DialogHeader>
        {!d ? <div className="p-8 text-muted-foreground">Loading…</div> : (
          <div className="overflow-y-auto flex-1 space-y-6 pr-1">
            {d.vehicle.maintenance_alert && (
              <div className="flex items-start gap-2 text-sm bg-amber-500/10 border border-amber-500/40 text-amber-600 dark:text-amber-400 px-3 py-2">
                <Wrench className="h-4 w-4 mt-0.5 shrink-0" /> <span><b>Maintenance:</b> {d.vehicle.maintenance_alert}</span>
              </div>
            )}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
              <Stat label="Trips" value={String(s.trips)} icon={Route} />
              <Stat label="Earnings" value={s.earnings} accent="text-emerald-500" icon={TrendingUp} />
              <Stat label="Expenses" value={s.expenses} accent="text-primary" icon={Receipt} />
              <Stat label="Profit / Loss" value={s.profit} accent={s.profit >= 0 ? "text-emerald-500" : "text-red-500"} icon={Wallet} />
            </div>

            {/* Add expense */}
            <section className="border border-primary/40 bg-primary/5 p-4 space-y-3">
              <div className="flex items-center gap-2 text-sm font-bold uppercase tracking-wide"><Plus className="h-4 w-4" /> Add Expense</div>
              <div className="grid sm:grid-cols-4 gap-2 items-end">
                <div className="space-y-1"><Label className="text-xs">Category</Label>
                  <Select value={exp.category} onValueChange={(v) => setExp({ ...exp, category: v })}>
                    <SelectTrigger className="rounded-none h-9 bg-card" data-testid="veh-exp-category"><SelectValue /></SelectTrigger>
                    <SelectContent>{(d.categories || EXP_CATEGORIES).map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="space-y-1"><Label className="text-xs">Amount (₹)</Label><Input type="number" className="rounded-none bg-card h-9 font-mono-num" value={exp.amount} onChange={(e) => setExp({ ...exp, amount: e.target.value })} data-testid="veh-exp-amount" /></div>
                <div className="space-y-1"><Label className="text-xs">Date</Label><Input type="date" className="rounded-none bg-card h-9" value={exp.date} onChange={(e) => setExp({ ...exp, date: e.target.value })} data-testid="veh-exp-date" /></div>
                <Button className="rounded-none h-9 font-bold uppercase text-xs" onClick={addExpense} disabled={busy} data-testid="veh-exp-save">Add</Button>
              </div>
              <Input className="rounded-none bg-card h-9" placeholder="Remarks (optional)" value={exp.remarks} onChange={(e) => setExp({ ...exp, remarks: e.target.value })} data-testid="veh-exp-remarks" />
            </section>

            {/* Expenses list */}
            <section>
              <div className="text-xs font-bold uppercase tracking-wide mb-2">Expense Ledger ({d.expenses.length})</div>
              <div className="border border-border divide-y divide-border">
                {d.expenses.length === 0 && <div className="p-3 text-sm text-muted-foreground">No expenses recorded yet.</div>}
                {d.expenses.map((e) => (
                  <div key={e.id} className="flex items-center gap-3 p-2 px-3 text-sm" data-testid={`veh-expense-${e.id}`}>
                    <span className="w-32 truncate font-medium">{e.category}</span>
                    <span className="font-mono-num font-bold w-24">{money(e.amount)}</span>
                    {e.source === "order"
                      ? <span className="text-[10px] px-2 py-0.5 border border-border bg-muted uppercase" title={`From order ${e.order_number || ""}`}>Order {e.order_number || ""}</span>
                      : <span className="text-[10px] px-2 py-0.5 border border-emerald-500/40 bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 uppercase">Direct</span>}
                    <span className="text-xs text-muted-foreground flex-1 truncate">{e.date || fmtDate(e.created_at)}{e.remarks ? ` · ${e.remarks}` : ""}</span>
                    {e.source === "vehicle" && <Button size="icon" variant="ghost" className="rounded-none h-8 w-8 text-destructive" onClick={() => delExpense(e)} data-testid={`veh-exp-del-${e.id}`}><Trash2 className="h-4 w-4" /></Button>}
                  </div>
                ))}
              </div>
            </section>

            {/* Trips */}
            <section>
              <div className="text-xs font-bold uppercase tracking-wide mb-2">Self Bookings / Trips ({d.trips.length})</div>
              <div className="border border-border divide-y divide-border">
                {d.trips.length === 0 && <div className="p-3 text-sm text-muted-foreground">No self bookings assigned to this vehicle yet.</div>}
                {d.trips.map((t) => (
                  <div key={t.id} className="flex items-center gap-3 p-2 px-3 text-sm" data-testid={`veh-trip-${t.order_number}`}>
                    <span className="font-mono-num text-primary font-bold w-24">{t.order_number}</span>
                    <span className="flex-1 truncate"><span className="font-medium">{t.customer_name}</span> <span className="text-xs text-muted-foreground">· {t.route}</span></span>
                    <span className="text-xs text-muted-foreground w-20">{fmtDate(t.date)}</span>
                    <span className="font-mono-num font-bold text-emerald-500 w-24 text-right">{money(t.earning)}</span>
                  </div>
                ))}
              </div>
            </section>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
